import React, { useState, useEffect, useRef } from 'react';
import { listAgents } from '../services/api';
import './AssistantDropdown.css';
import { matchesSearch } from '../utils/search';

const AssistantDropdown = ({ value, onChange, placeholder = "Select assistant..." }) => {
  const [assistants, setAssistants] = useState([]);
  const [filteredAssistants, setFilteredAssistants] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Fetch agents (V3 list — prefers `agents`, falls back to legacy)
  useEffect(() => {
    const fetchAssistants = async () => {
      setLoading(true);
      try {
        const response = await listAgents();
        const rows = Array.isArray(response.data?.data) ? response.data.data : [];
        const sorted = [...rows].sort((a, b) =>
          (a.name || '').localeCompare(b.name || '')
        );
        setAssistants(sorted);
        setFilteredAssistants(sorted);
      } catch (error) {
        console.error('Error fetching assistants:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAssistants();
  }, []);

  // Filter assistants based on search term
  useEffect(() => {
    if (!searchTerm) {
      setFilteredAssistants(assistants);
    } else {
      const filtered = assistants.filter(assistant => 
        matchesSearch(searchTerm, assistant.name, assistant.assistant_id)
      );
      setFilteredAssistants(filtered);
    }
  }, [searchTerm, assistants]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelect = (assistant) => {
    onChange(assistant.assistant_id);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
  };

  const handleInputClick = () => {
    setIsOpen(true);
  };

  const getDisplayValue = () => {
    if (!value) return '';
    const selectedAssistant = assistants.find(a => a.assistant_id === value);
    return selectedAssistant ? `${selectedAssistant.name} (${selectedAssistant.assistant_id})` : value;
  };

  return (
    <div className="assistant-dropdown" ref={dropdownRef}>
      <input
        type="text"
        value={isOpen ? searchTerm : getDisplayValue()}
        onChange={handleInputChange}
        onClick={handleInputClick}
        placeholder={placeholder}
        className="assistant-dropdown-input"
        readOnly={!isOpen}
      />
      {isOpen && (
        <div className="assistant-dropdown-menu">
          {loading ? (
            <div className="dropdown-loading">Loading assistants...</div>
          ) : filteredAssistants.length === 0 ? (
            <div className="dropdown-no-results">No assistants found</div>
          ) : (
            filteredAssistants.map((assistant) => (
              <div
                key={assistant.assistant_id}
                className="dropdown-option"
                onClick={() => handleSelect(assistant)}
              >
                <div className="assistant-name">{assistant.name}</div>
                <div className="assistant-id">{assistant.assistant_id}</div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default AssistantDropdown;
